/** CustomEvent name for the fixed bottom error banner (Paper 2XH / rate-limit style). */
export const APP_ERROR_EVENT = 'corner:app-error';

export type AppErrorDetail = {
  title: string;
  description?: string;
  /** 0 = no auto-dismiss */
  autoDismissMs?: number;
};

export function showAppError(title: string, description?: string, autoDismissMs = 6000) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<AppErrorDetail>(APP_ERROR_EVENT, {
      detail: { title, description, autoDismissMs },
    })
  );
}

function pickServerMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const d = data as Record<string, unknown>;
  if (typeof d.message === 'string') return d.message;
  if (typeof d.error === 'string') return d.error;
  if (Array.isArray(d.errors) && typeof d.errors[0] === 'string') return d.errors[0];
  return undefined;
}

/** Map HTTP status + optional JSON body to user-facing banner (used by axios interceptor).
 *  Accepts optional app context for plan/usage-aware messaging (e.g. 402 quota errors). */
export function showAxiosErrorBanner(
  status: number,
  data: unknown,
  context?: { userPlan?: 'free' | 'pro' | 'enterprise'; usage?: { aiRequestsThisMonth: number; pagesThisMonth: number } },
) {
  const server = pickServerMessage(data);
  if (status === 429) {
    showAppError('Too many requests', 'Please wait a moment before trying again.', 5000);
    return;
  }
  switch (status) {
    case 400:
      showAppError('Bad request', server ?? 'Something was wrong with that request.');
      return;
    case 401:
      showAppError('Sign in required', server ?? 'Your session may have expired. Sign in and try again.');
      return;
    case 402: {
      // Plan-limit errors — show contextual messaging when plan context is available
      if (context?.userPlan === 'free') {
        const usageNote = context.usage
          ? ` You've used ${context.usage.aiRequestsThisMonth} requests this month.`
          : '';
        showAppError('Plan limit reached', `You've reached your free plan limit.${usageNote} Upgrade to Pro for unlimited access.`);
      } else {
        showAppError('Upgrade needed', server ?? 'This action requires an upgraded plan.');
      }
      return;
    }
    case 403:
      showAppError('Access denied', server ?? "You don't have permission to do that.");
      return;
    case 404:
      showAppError('Not found', server ?? 'That resource could not be found.');
      return;
    case 408:
      showAppError('Request timed out', server ?? 'The request took too long. Try again.');
      return;
    case 413:
      showAppError('File too large', server ?? 'Try a smaller file.');
      return;
    case 502:
    case 504:
      showAppError('Gateway error', server ?? 'Something went wrong upstream. Try again.');
      return;
    case 503:
      showAppError('Service unavailable', server ?? 'Please try again in a few minutes.');
      return;
    case 500:
      showAppError('Server error', server ?? 'Something went wrong on our end. Try again.');
      return;
    default:
      if (status >= 400) {
        showAppError('Something went wrong', server ?? `Request failed (${status}). Try again.`);
      }
  }
}

export function showNetworkErrorBanner() {
  showAppError('Connection problem', 'Check your internet connection and try again.');
}
